
import React, { useState } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TeacherSpecializedTutoring } from '@/hooks/useSpecializedTutorings';

interface SpecializedTutoringTableProps {
  tutorings: TeacherSpecializedTutoring[];
  onEdit: (tutoring: TeacherSpecializedTutoring) => void;
  onDelete: (id: string) => Promise<boolean>;
}

export function SpecializedTutoringTable({ tutorings, onEdit, onDelete }: SpecializedTutoringTableProps) {
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    tutoringId?: string;
    tutoringName?: string;
  }>({ isOpen: false });

  const handleDeleteClick = (id: string, tutoringName: string) => {
    setDeleteDialog({
      isOpen: true,
      tutoringId: id,
      tutoringName,
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.tutoringId) {
      await onDelete(deleteDialog.tutoringId);
    }
    setDeleteDialog({ isOpen: false });
  };

  if (tutorings.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        ยังไม่มีข้อมูลการติวสอบพิเศษ
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>รายการติวสอบพิเศษ</TableHead>
              <TableHead>รายละเอียด</TableHead>
              <TableHead className="w-24">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tutorings.map((tutoring) => (
              <TableRow key={tutoring.id}>
                <TableCell className="font-medium">
                  {tutoring.specialized_tutorings}
                </TableCell>
                <TableCell>
                  {tutoring.description || '-'}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEdit(tutoring)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(tutoring.id, tutoring.specialized_tutorings)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => setDeleteDialog({ isOpen: open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบ</AlertDialogTitle>
            <AlertDialogDescription>
              คุณต้องการลบการติวสอบพิเศษ "{deleteDialog.tutoringName}" ใช่หรือไม่?
              <br />
              <span className="text-red-600 font-medium">การดำเนินการนี้ไม่สามารถยกเลิกได้</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              ลบ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
